/**
 * Priority Finder — assessment data, scoring, and prompt-to-pillar mapping.
 *
 * The assessment scores a BCE buyer across the five CORES® pillars. Each question
 * has three answer options (green / amber / red) worth 3 / 2 / 1 points respectively.
 *
 * Prompt-to-pillar mapping (adjust freely if the product taxonomy shifts):
 *   C — Cash Flow:       bce_004 (Client Data & Revenue Map), bce_005 (Cash Flow File Organiser)
 *   O — Operating Budgets: bce_006 (Subscription & Software Audit)
 *   R — Records:         bce_001 (Folder Architecture), bce_002 (Financial Document Hub),
 *                        bce_003 (Contract Vault), bce_007 (Compliance & Renewal Tracker)
 *   E — Equity & Assets: bce_008 (Brand Asset Library), bce_009 (Content Catalog),
 *                        bce_012 (Client Feedback Analyser)
 *   S — Structure & Succession: bce_010 (Task Breakdown), bce_011 (Team Reference)
 */

export type PillarId = 'C' | 'O' | 'R' | 'E' | 'S';
export type AnswerColor = 'green' | 'amber' | 'red';

export interface PFOption {
  color: AnswerColor;
  text: string;
  points: 1 | 2 | 3;
}

export interface PFQuestion {
  id: string;
  text: string;
  options: [PFOption, PFOption, PFOption];
}

export interface PFSection {
  id: PillarId;
  letter: string;
  name: string;
  tagline: string;
  questions: PFQuestion[];
  promptIds: string[];
}

export const PILLAR_LABELS: Record<PillarId, string> = {
  C: 'Cash Flow',
  O: 'Operating Budgets',
  R: 'Records',
  E: 'Equity & Assets',
  S: 'Structure & Succession',
};

export const SECTIONS: PFSection[] = [
  {
    id: 'C',
    letter: 'C',
    name: 'Cash Flow',
    tagline: 'Where money comes in, goes out, and gets stuck.',
    promptIds: ['bce_004', 'bce_005'],
    questions: [
      {
        id: 'q1',
        text: 'Can you see exactly how much cash came in and went out last month without logging into your bank?',
        options: [
          { color: 'green', points: 3, text: 'Yes. I have a summary, spreadsheet, or report I can check right now.' },
          { color: 'amber', points: 2, text: "Roughly. I'd need to log in and scan transactions, but I could estimate." },
          { color: 'red', points: 1, text: "No. I'd need to export statements and manually add things up." },
        ],
      },
      {
        id: 'q2',
        text: 'Do you know how many active subscriptions and recurring charges your business has right now?',
        options: [
          { color: 'green', points: 3, text: 'Yes. I have a list and I review it regularly.' },
          { color: 'amber', points: 2, text: "I know the major ones, but there are probably some I've forgotten." },
          { color: 'red', points: 1, text: "I genuinely don't know. I'd have to go through my bank statements to find out." },
        ],
      },
      {
        id: 'q3',
        text: 'Could you produce a 3-month cash flow summary in under 10 minutes if someone asked?',
        options: [
          { color: 'green', points: 3, text: 'Yes. The data is organised and accessible.' },
          { color: 'amber', points: 2, text: 'I could pull something together, but it would take longer than 10 minutes.' },
          { color: 'red', points: 1, text: "No. I'd need to gather files from multiple places and build it from scratch." },
        ],
      },
    ],
  },
  {
    id: 'O',
    letter: 'O',
    name: 'Operating Budgets',
    tagline: 'Whether you are running to a plan or reacting to what is in the bank.',
    promptIds: ['bce_006'],
    questions: [
      {
        id: 'q4',
        text: 'Do you have a written budget or spending plan you actually check against each month?',
        options: [
          { color: 'green', points: 3, text: 'Yes. I have a budget and I compare actuals against it regularly.' },
          { color: 'amber', points: 2, text: "I have a rough idea of what I should spend, but it's not written down or tracked." },
          { color: 'red', points: 1, text: 'No. I react to what is in the bank rather than running against a plan.' },
        ],
      },
      {
        id: 'q5',
        text: 'When was the last time you audited your software and subscription costs?',
        options: [
          { color: 'green', points: 3, text: 'Within the last 3 months.' },
          { color: 'amber', points: 2, text: 'Sometime in the last year, but not recently.' },
          { color: 'red', points: 1, text: "I've never done a proper audit, or it's been over a year." },
        ],
      },
      {
        id: 'q6',
        text: "If your revenue dropped 20% tomorrow, do you know exactly which costs you'd cut first?",
        options: [
          { color: 'green', points: 3, text: 'Yes. I know which expenses are essential and which are discretionary.' },
          { color: 'amber', points: 2, text: "I have a general sense, but I'd need to sit down and figure out the specifics." },
          { color: 'red', points: 1, text: "I'd be scrambling. I don't have a clear picture of what's essential versus optional." },
        ],
      },
    ],
  },
  {
    id: 'R',
    letter: 'R',
    name: 'Records',
    tagline: 'How findable your files, contracts, and compliance dates are.',
    promptIds: ['bce_001', 'bce_002', 'bce_003', 'bce_007'],
    questions: [
      {
        id: 'q7',
        text: 'If I asked you to find a specific contract signed 18 months ago, how long would it take?',
        options: [
          { color: 'green', points: 3, text: 'Under 2 minutes. I know exactly where it is.' },
          { color: 'amber', points: 2, text: "5 to 15 minutes. I'd need to search through folders or email." },
          { color: 'red', points: 1, text: "I honestly don't know where it would be. It could take an hour or more." },
        ],
      },
      {
        id: 'q8',
        text: 'Are your financial documents (invoices, receipts, bank statements) organised by type and date?',
        options: [
          { color: 'green', points: 3, text: 'Yes. Everything is filed in a consistent structure.' },
          { color: 'amber', points: 2, text: 'Some are organised, but others are in email, downloads, or scattered across drives.' },
          { color: 'red', points: 1, text: "No. They're wherever they landed when I received them." },
        ],
      },
      {
        id: 'q9',
        text: 'Do you have a single folder structure for your entire business?',
        options: [
          { color: 'green', points: 3, text: 'Yes. One clear structure that covers everything.' },
          { color: 'amber', points: 2, text: "I have folders, but they've grown organically and aren't consistent." },
          { color: 'red', points: 1, text: 'Files are scattered across email, desktop, cloud drives, and downloads.' },
        ],
      },
      {
        id: 'q10',
        text: 'Is there a system tracking which compliance deadlines, renewals, and registrations are coming up?',
        options: [
          { color: 'green', points: 3, text: 'Yes. I have a tracker or calendar with all key dates.' },
          { color: 'amber', points: 2, text: "I track some deadlines, but I know there are things I'm probably missing." },
          { color: 'red', points: 1, text: 'No. Deadlines come to me as surprises or last-minute emails.' },
        ],
      },
    ],
  },
  {
    id: 'E',
    letter: 'E',
    name: 'Equity & Assets',
    tagline: 'Brand, content, and what your business owns beyond cash.',
    promptIds: ['bce_008', 'bce_009', 'bce_012'],
    questions: [
      {
        id: 'q11',
        text: 'Could a new team member or contractor use your brand (logo, colours, fonts, tone of voice) correctly without asking you?',
        options: [
          { color: 'green', points: 3, text: 'Yes. I have a brand guide or asset library they could reference.' },
          { color: 'amber', points: 2, text: "They could get close, but they'd probably need to ask me about some things." },
          { color: 'red', points: 1, text: "No. Everything is in my head or scattered across files. They'd have to ask me." },
        ],
      },
      {
        id: 'q12',
        text: "Do you know how many pieces of content you've created that could be repurposed or repackaged?",
        options: [
          { color: 'green', points: 3, text: 'Yes. I have a catalogue or inventory of my content assets.' },
          { color: 'amber', points: 2, text: "I know there's a lot, but I've never catalogued it systematically." },
          { color: 'red', points: 1, text: "No idea. Content goes out and I don't track what I've produced." },
        ],
      },
      {
        id: 'q13',
        text: 'Have you systematically reviewed what your clients actually say about you, the patterns in their feedback?',
        options: [
          { color: 'green', points: 3, text: "Yes. I've analysed feedback and know the recurring themes." },
          { color: 'amber', points: 2, text: "I've read individual reviews and testimonials, but never looked for patterns." },
          { color: 'red', points: 1, text: 'No. I collect feedback sporadically or not at all.' },
        ],
      },
    ],
  },
  {
    id: 'S',
    letter: 'S',
    name: 'Structure & Succession',
    tagline: 'Whether the business can run when you are not in the room.',
    promptIds: ['bce_010', 'bce_011'],
    questions: [
      {
        id: 'q14',
        text: 'If you disappeared for 30 days, could your business continue operating without you?',
        options: [
          { color: 'green', points: 3, text: 'Yes. The key processes are documented and someone could keep things running.' },
          { color: 'amber', points: 2, text: 'Partially. Some things would continue, but critical tasks would stall.' },
          { color: 'red', points: 1, text: 'No. The business depends on me being present for almost everything.' },
        ],
      },
      {
        id: 'q15',
        text: 'Is there a written breakdown of how your most common projects or tasks get done, step by step?',
        options: [
          { color: 'green', points: 3, text: 'Yes. I have documented SOPs or process guides.' },
          { color: 'amber', points: 2, text: 'Some tasks are documented, but most live in my head.' },
          { color: 'red', points: 1, text: "No. If I'm not doing it, nobody knows how it gets done." },
        ],
      },
      {
        id: 'q16',
        text: 'Does a single reference document exist that contains everything a team member needs to know to work in your business?',
        options: [
          { color: 'green', points: 3, text: "Yes. There's a handbook, wiki, or reference guide." },
          { color: 'amber', points: 2, text: "There's some documentation, but it's incomplete or outdated." },
          { color: 'red', points: 1, text: 'No. Onboarding is verbal and ad hoc.' },
        ],
      },
    ],
  },
];

export const TOTAL_MAX_POINTS = SECTIONS.reduce(
  (sum, s) => sum + s.questions.length * 3,
  0,
);

export interface SectionScore {
  id: PillarId;
  points: number;
  maxPoints: number;
  pct: number;
  rating: AnswerColor;
}

export interface PriorityFinderResult {
  completed_at: number;
  answers: Record<string, number>;
  overall_score: number;
  overall_pct: number;
  overall_rating: AnswerColor;
  section_scores: SectionScore[];
  implementation_order: string[];
}

export function ratingFromPct(pct: number): AnswerColor {
  if (pct >= 80) return 'green';
  if (pct >= 50) return 'amber';
  return 'red';
}

export function computeResult(answers: Record<string, number>): PriorityFinderResult {
  const section_scores: SectionScore[] = SECTIONS.map((section) => {
    const maxPoints = section.questions.length * 3;
    const points = section.questions.reduce((sum, q) => sum + (answers[q.id] ?? 0), 0);
    const pct = maxPoints === 0 ? 0 : Math.round((points / maxPoints) * 100);
    return { id: section.id, points, maxPoints, pct, rating: ratingFromPct(pct) };
  });

  const overall_score = section_scores.reduce((sum, s) => sum + s.points, 0);
  const overall_pct = Math.round((overall_score / TOTAL_MAX_POINTS) * 100);

  // Sort pillars by pct ascending (weakest first); tie-break by section order
  const orderedPillars = [...section_scores].sort((a, b) => {
    if (a.pct !== b.pct) return a.pct - b.pct;
    return SECTIONS.findIndex((s) => s.id === a.id) - SECTIONS.findIndex((s) => s.id === b.id);
  });

  const implementation_order: string[] = [];
  for (const pillar of orderedPillars) {
    const section = SECTIONS.find((s) => s.id === pillar.id);
    if (!section) continue;
    for (const promptId of section.promptIds) {
      if (!implementation_order.includes(promptId)) {
        implementation_order.push(promptId);
      }
    }
  }

  return {
    completed_at: Date.now(),
    answers,
    overall_score,
    overall_pct,
    overall_rating: ratingFromPct(overall_pct),
    section_scores,
    implementation_order,
  };
}

export function interpretationText(overall_pct: number): string {
  if (overall_pct < 40) {
    return "Your business is running on memory and manual effort across most areas. The good news: the systems you're about to build will have an outsized impact because you're starting from a low base. Focus on your lowest-scoring pillar first. That's where the biggest risk is hiding.";
  }
  if (overall_pct <= 65) {
    return 'You have some systems in place, but there are meaningful gaps that are costing you time and money. The Priority Matrix below shows you exactly where the gaps are. Start with the red pillars. Those represent the areas where you are most exposed.';
  }
  return "Your business is more organised than most. The gaps that remain are specific rather than systemic. Focus on the amber and red areas. Tightening those will move you from 'good enough' to genuinely well-run.";
}

export const STORAGE_KEY = (productId: string, bonusId: string) =>
  `aicores:${productId}:${bonusId}:result`;

export function loadResult(productId: string, bonusId: string): PriorityFinderResult | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY(productId, bonusId));
    return raw ? (JSON.parse(raw) as PriorityFinderResult) : null;
  } catch {
    return null;
  }
}

export function saveResult(
  productId: string,
  bonusId: string,
  result: PriorityFinderResult,
): void {
  try {
    window.localStorage.setItem(STORAGE_KEY(productId, bonusId), JSON.stringify(result));
  } catch {
    // Private mode / storage quota — nothing we can do. Result is still in memory for this session.
  }
}
